import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getGraphsTemplateById,
  loadSensorAverage,
  loadSensorHistory,
  loadStatusHistory
} from 'redux/graphTemplates/actions';
import {
  getDateRange,
  getFormattedDate,
  getLineStyle,
  PERIOD_TYPE_CUSTOM_RANG,
  PERIOD_TYPE_LAST_8_HOURS,
  periodTypeList
} from 'attrs/chartsTemplate';
import { getGraphsTemplatesReducerState } from 'redux/rootSelectors';
import { debounce, getMachineStatusProps, isError, isNumber, isSuccess } from 'helpers/utils';
import { useParams } from 'react-router-dom';
import ReactEcharts from 'echarts-for-react';
import { getMillSensor } from 'attrs/sensorType';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DateTimeDialog } from 'web-components-ts';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { MACHINE_STATUS_SIMPLE_MAP } from 'attrs/notifications';
import {
  autoRefreshPeriods,
  calculateValue,
  determineDirection,
  findClosestStatusDate,
  sideSpace,
  statusMap
} from 'helpers/chartUtils';
import PropTypes from 'prop-types';
import MobileSensorPortrait from 'components/elements/MobileSensorPortrait';
import { getValueToNullableCell } from 'helpers/stringHandler';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChartTemplateFooterContainer from '../../../../../components/ChartTemplateFooter';
import { DATA_EXPORT_MAX_PERIOD_IN_DAYS } from '../../../../../attrs/dataExport';

// Total height of the chart grid in pixels
const chartPixelHeight = 410;
// Total height of the chart container in pixels
const chartPixelHeightContainer = 580;

export const areChartDataRequestsFinished = statuses => statuses.every(status => isSuccess(status) || isError(status));

export default function ChartTemplateDetails({
  graphTemplate,
  interval,
  setInterval,
  customPeriodStart,
  setCustomPeriodStart,
  customPeriodEnd,
  setCustomPeriodEnd,
  chartRef,
  footerRef,
  setIsChartLoading
}) {
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));

  const xAxisDefaultValue = [
    {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: '{dd}/{MM}/{yyyy}\n{hh}:{mm}:{ss}'
      },
      axisPointer: {
        show: true,
        type: 'line',
        lineStyle: {
          color: 'gray',
          width: 1,
          type: 'dashed',
          shadowBlur: 3,
          shadowColor: 'rgba(0, 0, 0, 1)'
        }
      }
    }
  ];

  const yAxisDefaultValue = [
    {
      data: []
    }
  ];

  const seriesDefaultValue = [
    {
      data: [],
      type: 'line'
    }
  ];

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { templateId } = useParams();

  const [resumedSensorMap, setResumedSensorMap] = useState(new Map());
  const [sensorVisibilityList, setSensorVisibilityList] = useState([]);
  const [loadSensorParamsList, setLoadSensorParamsList] = useState([]);
  const [loadSensorAverageParamsList, setLoadSensorAverageParamsList] = useState([]);
  const [loadStatusParam, setLoadStatusParam] = useState();

  const loadSensorHistoryStatusEqual = (oldObj, newObj) =>
    oldObj?.loadSensorHistoryStatus?.status === newObj?.loadSensorHistoryStatus?.status;
  const { loadSensorHistoryStatus } = useSelector(getGraphsTemplatesReducerState, loadSensorHistoryStatusEqual);

  const loadSensorAverageStatusEqual = (oldObj, newObj) =>
    oldObj?.loadSensorAverageStatus?.status === newObj?.loadSensorAverageStatus?.status;
  const { loadSensorAverageStatus } = useSelector(getGraphsTemplatesReducerState, loadSensorAverageStatusEqual);

  const sensorHistoryResponseEqual = (oldObj, newObj) =>
    oldObj?.sensorHistoryResponse === newObj?.sensorHistoryResponse;
  const { sensorHistoryResponse } = useSelector(getGraphsTemplatesReducerState, sensorHistoryResponseEqual);

  const sensorAverageResponseEqual = (oldObj, newObj) =>
    oldObj?.sensorAverageResponse === newObj?.sensorAverageResponse;
  const { sensorAverageResponse } = useSelector(getGraphsTemplatesReducerState, sensorAverageResponseEqual);

  const { loadStatusHistoryStatus, statusHistoryResponse } = useSelector(getGraphsTemplatesReducerState);
  const [sensorHistoryMap, setSensorHistoryMap] = useState(new Map());
  const [sensorAverageMap, setSensorAverageMap] = useState(new Map());
  const [statusHistoryList, setStatusHistoryList] = useState([]);
  const [yAxis, setYAxis] = useState([]);
  const [series, setSeries] = useState([]);
  const [updateStatus, setUpdateStatus] = useState(false);
  const didMount = useRef(false);
  const [openCustomDateDialog, setOpenCustomDateDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const loadingTimeoutRef = useRef();
  const [openTooltip, setOpenTooltip] = useState(false);
  const [checkedShowMachineStatus, setCheckedShowMachineStatus] = useState(true);
  const [dataZoomRange, setDataZoomRange] = useState({ start: 0, end: 100 });
  const prevForceReloadRef = useRef();
  const [forceReload, setForceReload] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState('off');
  const autoRefreshTimerRef = useRef(null);

  const language = useMemo(() => i18n.language, [i18n.language]);

  useEffect(() => {
    setIsChartLoading(loading);
  }, [loading, setIsChartLoading]);

  useEffect(() => () => clearTimeout(loadingTimeoutRef.current), []);

  /**
   * A specific function to find sensor attributes; can be used only in this component
   * @param {*} resultsParam
   * @returns
   */
  function findSensorAttributes(resultsParam) {
    return resultsParam.map(result => {
      const sensor = graphTemplate.sensors.find(s => s.id === result.sensorId);
      const millSensor = getMillSensor(sensor.type);
      const name = t(millSensor?.name || sensor?.name);
      const unit = millSensor?.unit || sensor?.custom_unit || sensor?.unit || 'n/a';
      return {
        id: sensor.id,
        name,
        unit: sensor.is_static ? '' : unit,
        color: sensor.line_color,
        value: result.closestEntry?.v,
        isStatic: sensor.is_static
      };
    });
  }

  /**
   * Renders a colored circle only when the sensor is static.
   * If the sensor is not static (or is missing), it returns an empty string.
   *
   * @param {*} sensor The sensor object containing `isStatic` and `color`.
   * @returns {string} HTML string for the circle component or an empty string.
   */
  const renderCircle = sensor => {
    if (sensor?.isStatic) return '';
    return `
      <span
        style="
          display: inline-block;
          margin-right: 4px;
          border-radius: 10px;
          width: 10px;
          height: 10px;
          background-color: ${sensor?.color};
        "
      ></span>
    `;
  };

  useEffect(() => {
    // check if graphTemplate is already loaded
    if (graphTemplate && graphTemplate.id && prevForceReloadRef.current !== forceReload) {
      prevForceReloadRef.current = forceReload;
      clearTimeout(loadingTimeoutRef.current);
      setSeries([]);
      setSensorHistoryMap(new Map());
      setSensorAverageMap(new Map());
      setStatusHistoryList([]);
      setUpdateStatus(false);
      // needed to work with the status line
      setYAxis([
        {
          isStatusYAxis: true,
          type: 'value',
          min: 0,
          max: 100,
          show: false // hide this axis visually
        }
      ]);
      setLoading(true);
      let intervalSelection = interval;
      if (!intervalSelection) {
        intervalSelection = graphTemplate?.period_type || PERIOD_TYPE_LAST_8_HOURS;
        setInterval(intervalSelection);
      }
      const [pastDate, currentDate] =
        intervalSelection !== PERIOD_TYPE_CUSTOM_RANG
          ? getDateRange(intervalSelection)
          : [customPeriodStart, customPeriodEnd];
      const tmpResumedSensorMap = new Map();
      const tmpSensorVisibilityList = [];
      const tempSensorParamsList = [];
      const tempStatusParam = {
        machineId: graphTemplate.machine_id,
        from: pastDate,
        to: currentDate
      };
      graphTemplate.sensors.forEach((s, i) => {
        const millSensor = getMillSensor(s?.type);
        const name = millSensor?.name || s?.name;
        const unit = millSensor?.unit || s?.custom_unit || s?.unit || 'n/a';

        const resumedSensor = {
          sensorId: s?.id,
          name,
          isCustom: s?.is_custom,
          isStatic: s?.is_static,
          type: s?.type,
          customIconType: s?.custom_icon_type,
          staticIconType: s?.static_icon_type,
          unit,
          productionLine: s?.production_line,
          machineName: s?.machine_name,
          lineColor: s?.line_color,
          lineStyle: s?.line_style,
          lineThickness: s?.line_thickness
        };
        tmpResumedSensorMap.set(s?.id, resumedSensor);
        tmpSensorVisibilityList.push(true);
        tempSensorParamsList.push({
          sensorId: s?.id,
          machineId: s?.machine_id,
          from: pastDate,
          to: currentDate,
          unit,
          sensor_type: s?.type
        });

        const transLatedName = t(`${name}`);
        const yAxisSensor = {
          sensorId: s?.id,
          min: isNumber(s?.min_scale) ? s?.min_scale : 'dataMin',
          max: isNumber(s?.max_scale) ? s?.max_scale : 'dataMax',
          type: 'value',
          name: unit,
          show: false,
          axisLine: {
            show: true,
            lineStyle: {
              color: s?.line_color
            }
          },
          nameRotate: 45,
          nameTextStyle: {
            fontSize: 14,
            fontWeight: 'bold'
          },
          axisLabel: {
            show: true
          },
          splitLine: {
            show: false
          }
        };
        const seriesSensor = {
          sensorId: s?.id,
          name: transLatedName,
          type: 'line',
          symbol: 'none',
          color: s?.line_color,
          lineStyle: {
            width: s?.line_thickness || 1,
            type: getLineStyle(s?.line_style),
            simplify: true,
            opacity: 1
          },
          yAxisIndex: i + 1,
          data: [],
          show: true,
          itemStyle: { opacity: 1 },
          progressive: 5000,
          progressiveThreshold: 10000,
          large: true,
          largeThreshold: 2000,
          z: 2,
          zlevel: 0
        };
        setYAxis(prevYAxis => (!prevYAxis?.length ? [yAxisSensor] : [...prevYAxis, yAxisSensor]));
        setSeries(prevSeries => (!prevSeries?.length ? [seriesSensor] : [...prevSeries, seriesSensor]));
      });
      setResumedSensorMap(tmpResumedSensorMap);
      setSensorVisibilityList(tmpSensorVisibilityList);
      setLoadSensorParamsList(tempSensorParamsList);
      setLoadSensorAverageParamsList(tempSensorParamsList);
      setLoadStatusParam(tempStatusParam);
      dispatch(loadSensorHistory(tempSensorParamsList));
      dispatch(loadSensorAverage(tempSensorParamsList));
      dispatch(loadStatusHistory(tempStatusParam));
    }
  }, [dispatch, forceReload, graphTemplate]);

  // when changes period type
  useEffect(() => {
    if (
      interval &&
      (interval !== PERIOD_TYPE_CUSTOM_RANG || (customPeriodStart && customPeriodEnd)) &&
      graphTemplate &&
      graphTemplate.id
    ) {
      if (didMount.current) {
        setLoading(true);
        setDataZoomRange({ start: 0, end: 100 });
        setForceReload(prev => !prev);
      } else {
        // This code will run only on the initial mount
        didMount.current = true;
      }
    }
  }, [interval, customPeriodStart, customPeriodEnd, graphTemplate]);

  useEffect(() => {
    if (isSuccess(loadSensorHistoryStatus.status)) {
      setSensorHistoryMap(sensorHistoryResponse);
    }
  }, [loadSensorHistoryStatus, sensorHistoryResponse]);

  useEffect(() => {
    if (isSuccess(loadSensorAverageStatus.status)) {
      setSensorAverageMap(sensorAverageResponse);
    }
  }, [loadSensorAverageStatus, sensorAverageResponse]);

  useEffect(() => {
    if (sensorHistoryMap.size && graphTemplate && graphTemplate.id) {
      const isStaticMap = new Map();
      sensorHistoryMap.forEach((value, key) => {
        const foundSensor = graphTemplate?.sensors?.find(s => s.id === key);
        const isStatic = foundSensor?.is_static || false;
        isStaticMap.set(key, isStatic);
        const machineId = foundSensor?.machine_id;
        const type = foundSensor?.type;
        const customUnit = foundSensor?.custom_unit;
        const unit = foundSensor?.unit;
        const millSensor = getMillSensor(type);
        const unitDef = millSensor?.unit || customUnit || unit || 'n/a';

        const newValue =
          value?.map(m => ({
            name: new Date(m.t).toISOString(),
            value: [new Date(m.t).toISOString(), m.v],
            unit: isStatic ? '' : unitDef,
            machineId,
            sensorId: key,
            isStatic,
            time: m.t
          })) || [];

        setSeries(prevSeries => prevSeries.map(s => (s?.sensorId === key ? { ...s, data: newValue } : s)));
      });

      let yAxisIndex = 0;
      setYAxis(prevYAxis =>
        prevYAxis.map(y => {
          if (isStaticMap.get(y.sensorId)) return y;
          const willShow = sensorHistoryMap.has(y.sensorId);
          const ny = {
            ...y,
            show: willShow,
            position: determineDirection(yAxisIndex),
            offset: calculateValue(yAxisIndex)
          };
          if (willShow) yAxisIndex += 1;
          return ny;
        })
      );
    }
  }, [sensorHistoryMap, setSeries]);

  useEffect(() => {
    if (isSuccess(loadStatusHistoryStatus.status) && !updateStatus) {
      setStatusHistoryList(statusHistoryResponse);
      setUpdateStatus(true);
    }
  }, [loadStatusHistoryStatus, statusHistoryResponse]);

  useEffect(() => {
    if (
      !loadStatusParam ||
      !areChartDataRequestsFinished([
        loadSensorHistoryStatus.status,
        loadSensorAverageStatus.status,
        loadStatusHistoryStatus.status
      ])
    ) {
      return;
    }

    const totalItems = Array.from(sensorHistoryMap.values()).reduce(
      (max, sensorHistory) => Math.max(max, sensorHistory?.length || 0),
      0
    );
    const delay = Math.max(totalItems * 0.24, 1800);

    clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => {
      setLoading(false);
    }, delay);
  }, [
    loadSensorAverageStatus.status,
    loadSensorHistoryStatus.status,
    loadStatusHistoryStatus.status,
    loadStatusParam,
    sensorHistoryMap
  ]);

  useEffect(() => {
    // 'hasStatusSeries' is only used because of hot reload in strict mode
    const hasStatusSeries = series.some(s => s.name.includes('STATUS-'));

    if (sensorHistoryMap?.size && statusHistoryList?.length && (updateStatus || !hasStatusSeries)) {
      setUpdateStatus(false);

      // Build periods from the statusHistoryList
      const statusPeriods = [];
      statusHistoryList.forEach((status, index) => {
        const nextItem = index < statusHistoryList.length - 1 ? statusHistoryList[index + 1] : status;
        const strCurrentDate = new Date(status.t).toISOString();
        const strNextDate = new Date(nextItem.t).toISOString();
        statusPeriods.push({
          start: strCurrentDate,
          end: strNextDate,
          status: status.v,
          color: statusMap[status.v],
          warning: status.w
        });
      });

      // Build series objects from periods
      const built = Object.values(
        statusPeriods.reduce((acc, period) => {
          const statusKey = `STATUS-${period.status}`;

          if (!acc[statusKey]) {
            acc[statusKey] = {
              name: statusKey,
              type: 'line',
              smooth: false,
              show: checkedShowMachineStatus,
              symbol: 'none',
              yAxisIndex: 0,
              z: 1,
              zlevel: 0,
              lineStyle: {
                width: 12,
                color: period.color,
                simplify: true,
                opacity: checkedShowMachineStatus ? 0.6 : 0
              },
              data: []
            };
          }

          // Start the period with the beginning point
          acc[statusKey].data.push({
            name: new Date(period.start).toISOString(),
            value: [new Date(period.start).toISOString(), 1.6],
            time: period.start
          });

          // End the period with the ending point and add a null to create a gap
          acc[statusKey].data.push(
            {
              name: new Date(period.end).toISOString(),
              value: [new Date(period.end).toISOString(), 1.6],
              time: period.end
            },
            null // gap between this and the next period
          );

          if (period.warning) {
            const warningKey = 'STATUS-WARNING';
            if (!acc[warningKey]) {
              acc[warningKey] = {
                name: warningKey,
                type: 'line',
                smooth: false,
                show: true,
                symbol: 'none',
                yAxisIndex: 0,
                z: 1,
                zlevel: 0,
                lineStyle: {
                  width: 6,
                  color: '#F8CC84',
                  simplify: true,
                  opacity: 0.6
                },
                data: []
              };
            }

            acc[warningKey].data.push({
              name: new Date(period.start).toISOString(),
              value: [new Date(period.start).toISOString(), 3.9],
              time: period.start
            });

            acc[warningKey].data.push(
              {
                name: new Date(period.end).toISOString(),
                value: [new Date(period.end).toISOString(), 3.9],
                time: period.end
              },
              null
            );
          }

          return acc;
        }, {})
      );

      setSeries(prev => (!prev?.length ? built : [...prev.filter(s => !s.name.includes('STATUS-')), ...built]));
    }
  }, [sensorHistoryMap, statusHistoryList, updateStatus, checkedShowMachineStatus, series]);

  // need to make this to be unable to zoom out of range %0 to %100
  const validateZoomRange = (start, end) => ({
    start: Math.max(0, Math.min(start, 100)),
    end: Math.max(0, Math.min(end, 100))
  });

  useEffect(() => {
    const chartInstance = chartRef?.current?.getEchartsInstance();
    if (!chartInstance) return;

    const onZoomRaw = params => {
      const zoom = params.batch ? params.batch[0] : params;
      setDataZoomRange(validateZoomRange(zoom.start, zoom.end));

      if (!graphTemplate?.sensors?.length) return;

      const allDataPoints = series.flatMap(s => s.data).filter(p => p && p.time);

      if (!allDataPoints.length) return;

      const sorted = allDataPoints.slice().sort((a, b) => new Date(a.time) - new Date(b.time));
      const minTime = new Date(sorted[0].time);
      const maxTime = new Date(sorted[sorted.length - 1].time);

      const startTime = new Date(minTime.getTime() + ((maxTime - minTime) * zoom.start) / 100);
      const endTime = new Date(minTime.getTime() + ((maxTime - minTime) * zoom.end) / 100);

      const averageSensorParamsList = graphTemplate.sensors.map(s => {
        const millSensor = getMillSensor(s?.type);
        const unit = millSensor?.unit || s?.custom_unit || s?.unit || 'n/a';
        return {
          sensorId: s?.id,
          machineId: s?.machine_id,
          from: startTime.toISOString(),
          to: endTime.toISOString(),
          unit,
          sensor_type: s?.type
        };
      });

      setLoadSensorAverageParamsList(averageSensorParamsList);
      dispatch(loadSensorAverage(averageSensorParamsList));
    };

    const onResizeRaw = () => {
      if (chartRef.current) chartRef.current.getEchartsInstance().resize();
    };

    const onZoom = debounce(onZoomRaw, 400);
    const onResize = debounce(onResizeRaw, 200);

    chartInstance.on('dataZoom', onZoom);
    window.addEventListener('resize', onResize);

    // proper cleanup need to be an arrow function, but ESLint warns about consistent-return
    // eslint-disable-next-line consistent-return
    return () => {
      chartInstance.off('dataZoom', onZoom);
      window.removeEventListener('resize', onResize);
      onZoom.cancel?.();
      onResize.cancel?.();
    };
  }, [chartRef, validateZoomRange, dispatch, loadSensorAverage]); // <- nada de `series` aqui

  function getLeftPadding() {
    return sideSpace(sensorHistoryMap?.size)[0];
  }

  function getRightPadding() {
    return sideSpace(sensorHistoryMap?.size)[1];
  }

  const options = useMemo(
    () => ({
      textStyle: {
        fontFamily: 'Roboto, sans-serif'
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(43, 47, 66, 0.96)',
        textStyle: {
          color: '#ffffff',
          fontFamily: 'Roboto, sans-serif'
        },
        confine: true, // Ensure tooltip stays within chart area
        position(point, _params, _dom, _rect, size) {
          // Point is the mouse position, size is the size of the tooltip
          const [x, y] = point;
          const { contentSize } = size;
          const posX = x + contentSize[0] > size.viewSize[0] - 42 ? x - contentSize[0] - 42 : x + 42;
          const posY = y + contentSize[1] > size.viewSize[1] ? y - contentSize[1] : y;
          return [posX, posY];
        },
        formatter(params) {
          let result = '';
          const currentTooltipTime = params[0].axisValue;
          if (statusHistoryList.length) {
            const status = findClosestStatusDate(currentTooltipTime, statusHistoryList);
            if (status?.v) {
              const hasWarning = status.w;
              const statusDetail = getMachineStatusProps(status.v);
              const statusWarning = t('enum.machine_status.WARNING').toUpperCase();
              const statusDesc = t(`enum.machine_status.${statusDetail.value}`).toUpperCase();
              const ColorSquare = `
                <span
                  style="
                    display:inline-block;
                    margin-right:5px;
                    width:10px;
                    height:10px;
                    background-color:${statusDetail.color};
                  "
                ></span>`;
              const Chip = hasWarning
                ? `
                  <div
                    style="
                      background-color: #ffcc00;
                      color: white;
                      padding: 3px 6px;
                      border-radius: 16px;
                      display: inline-block;
                      font-size: 12px;
                      line-height: 12px;
                    "
                  >
                    ${statusWarning}
                  </div>
                `
                : '';
              result += `${ColorSquare}${statusDesc}${Chip}<br/><br/>`;
            }
          }

          // Extract all sensorIds from params
          const sensorIdsInParams = new Set(params.map(p => p.data.sensorId));

          // Filter the map to exclude entries with sensorIds in params
          const filteredSensorHistory = [...sensorHistoryMap].filter(([sensorId]) => !sensorIdsInParams.has(sensorId));

          // Find the closest time for each filtered entry
          const nearestSensors = filteredSensorHistory.map(([sensorId, historyList]) => {
            // Check if the historyList is empty before trying to reduce
            if (!historyList || historyList.length === 0) {
              return {
                sensorId,
                closestEntry: null // or handle this case as needed
              };
            }
            // Find the entry with the closest time to targetTime
            const closestEntry = historyList.reduce((prev, curr) =>
              Math.abs(curr.t - currentTooltipTime) < Math.abs(prev.t - currentTooltipTime) ? curr : prev
            );
            return {
              sensorId,
              closestEntry
            };
          });

          // Iterate over params and find the sensor details
          const paramAttributes = params.reduce((acc, param) => {
            if (!param.seriesName.includes('STATUS-')) {
              acc.push({
                id: param.data.sensorId,
                name: param.seriesName,
                isStatic: param.data.isStatic,
                unit: param.data.unit,
                color: param.color,
                value: param.value[1]
              });
            }
            return acc;
          }, []);

          // Iterate over the graphTemplate sensors list to get sensor details
          const nearestSensorsAttribute = findSensorAttributes(nearestSensors);

          // Combine sensors in params and nearest sensors
          const combinedList = paramAttributes
            .concat(nearestSensorsAttribute)
            .filter((o, i, arr) => arr.findIndex(x => x.id === o.id) === i);

          // Sort sensorAttributes alphabetically by 'name'
          combinedList.sort((a, b) => a.name.localeCompare(b.name));

          const solidLine = '<div style="border-top: 2px solid #FFFFFF;margin: 6px 0;"></div>';
          result += combinedList
            .map(
              (sensor, i) => `
                ${i > 0 ? solidLine : ''}
                ${renderCircle(sensor)}${sensor.name}<br/>
                <span style="font-size:16px;font-weight:bold;margin: 6px 0;">${getValueToNullableCell(sensor.value)} ${
                sensor.unit
              }</span>`
            )
            .join('<br/>');
          if (combinedList.length > 0) result += solidLine;
          result += `<p style="margin: 3px 0;"> ${getFormattedDate(currentTooltipTime)}</p>`;
          return result;
        }
      },
      grid: {
        top: '80px',
        left: getLeftPadding(),
        right: getRightPadding(),
        height: `${chartPixelHeight}px`
      },
      xAxis: xAxisDefaultValue,
      yAxis: yAxis.length > 0 ? yAxis : yAxisDefaultValue,
      dataZoom: [
        {
          type: 'slider',
          filterMode: 'none', // Prevents the chart from clipping the line when zooming
          start: dataZoomRange.start,
          end: dataZoomRange.end,
          labelFormatter(value) {
            return `${getFormattedDate(value)}   `;
          }
        }
      ],
      series: series.length > 0 ? series : seriesDefaultValue
    }),
    [yAxis, series, statusHistoryList, sensorHistoryMap]
  );

  /**
   * Clears the existing auto-refresh timer if it is currently active.
   * If the timer is cleared, the reference to it is set to null.
   */
  function clearAutoRefreshTimer() {
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
  }

  const onChangeInterval = event => {
    const intervalParam = event.target.value;
    if (intervalParam !== PERIOD_TYPE_CUSTOM_RANG) setInterval(intervalParam);
    if (intervalParam !== PERIOD_TYPE_LAST_8_HOURS) {
      setAutoRefresh('off');
      clearAutoRefreshTimer();
    }
  };

  const handleOpenCustomDateDialog = () => {
    setOpenCustomDateDialog(true);
  };

  const handleCloseCustomDateDialog = () => {
    setOpenCustomDateDialog(false);
  };

  const handleSetValuesCustomPeriod = (customPeriodStartParam, customPeriodEndParam) => {
    setInterval(PERIOD_TYPE_CUSTOM_RANG);
    setCustomPeriodStart(customPeriodStartParam);
    setCustomPeriodEnd(customPeriodEndParam);
    setAutoRefresh('off');
    clearAutoRefreshTimer();
  };

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };

  const handleTooltipOpen = () => {
    setOpenTooltip(true);
  };

  const toggleStatusVisibility = prevSeries =>
    prevSeries.map(s => {
      if (s.name.includes('STATUS-')) {
        const show = !s.show;
        return {
          ...s,
          show,
          lineStyle: { ...s.lineStyle, opacity: show ? 0.6 : 0 }
        };
      }
      return s;
    });

  const handleCheckboxChange = event => {
    setCheckedShowMachineStatus(event.target.checked);
    setSeries(prevSeries => toggleStatusVisibility(prevSeries));
  };

  const handleRefreshInfo = () => {
    dispatch(getGraphsTemplateById(templateId));
  };

  const toggleSeriesVisibility = (prevSeries, index) =>
    prevSeries.map((s, i) => {
      if (i === index) {
        const show = !s.show;
        return {
          ...s,
          show,
          itemStyle: { opacity: show ? 1 : 0 },
          lineStyle: { ...s.lineStyle, opacity: show ? 1 : 0 }
        };
      }
      return s;
    });

  const toggleLineVisibility = index => {
    setSensorVisibilityList(sensorVisibilityList.map((s, i) => (i === index ? !s : s)));
    setSeries(prevSeries => toggleSeriesVisibility(prevSeries, index));
  };

  /**
   * Auto refresh interval
   * Gets the autoRefresh value to create an interval based on value, and calls loadSensorHistory and loadSensorAverage
   * to load the new values
   *
   * The autoRefreshPeriods contains the "value" field with the milliseconds of the interval
   */
  useEffect(() => {
    // always clear any previous timer
    clearAutoRefreshTimer();

    if (!graphTemplate?.id) return;

    // resolve interval ms from the key
    const selected = autoRefreshPeriods.find(p => p.key === autoRefresh);
    const intervalMs = selected?.value ?? 0;
    if (!intervalMs) return; // if 'off' is selected means nothing to schedule

    const getData = () => {
      const [fromDate, toDate] = getDateRange(interval);

      // build incremental windows without mutating sources
      const buildIncrementalSensorParams = () =>
        (loadSensorParamsList || []).map(item => ({
          ...item,
          from: fromDate,
          to: toDate
        }));

      // averages only need to extend 'to' param to nowIso
      const buildAvgSensorParams = () =>
        (loadSensorAverageParamsList || []).map(item => ({
          ...item,
          from: fromDate,
          to: toDate
        }));

      const buildIncrementalStatusParam = () => {
        if (!loadStatusParam) return null;
        return {
          ...loadStatusParam,
          from: fromDate,
          to: toDate
        };
      };

      const sensorParams = buildIncrementalSensorParams();
      const avgParams = buildAvgSensorParams();
      const statusParam = buildIncrementalStatusParam();

      if (sensorParams.length) dispatch(loadSensorHistory(sensorParams));
      if (dataZoomRange.start === 0 && dataZoomRange.end === 100) {
        if (avgParams.length) dispatch(loadSensorAverage(avgParams));
      }
      if (statusParam) dispatch(loadStatusHistory(statusParam));
    };

    // run at startup
    if (!loading) getData();

    // then on interval
    autoRefreshTimerRef.current = window.setInterval(getData, intervalMs);

    // proper cleanup need to be an arrow function, but ESLint warns about consistent-return
    // eslint-disable-next-line consistent-return
    return () => {
      clearAutoRefreshTimer();
    };
  }, [autoRefresh, graphTemplate?.id, loadSensorParamsList, loadSensorAverageParamsList, loadStatusParam, dispatch]);

  return (
    <Paper
      sx={{
        overflow: 'hidden',
        width: '100%',
        p: { xs: 1, sm: 2, md: 2, lg: 3 }
      }}
      data-testid="chart-template-details"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={9.5} lg sx={{ order: { xs: 1, sm: 1, md: 1, lg: 1 }, display: 'flex' }}>
          <FormControl
            data-testid="period-time-template-combobox"
            variant="filled"
            sx={{ minWidth: { xs: '100%', sm: '100%', md: '305px' } }}
            disabled={loading}
          >
            <InputLabel id="template-label">{t('chart_template_details.filter_time')}</InputLabel>
            <Select
              IconComponent={KeyboardArrowDownIcon}
              labelId="period-type-label"
              id="period-type-select"
              onChange={onChangeInterval}
              value={interval}
            >
              {periodTypeList.map(periodType => (
                <MenuItem key={periodType} value={periodType}>
                  {t(`enum.chart_templates.period.${periodType}`)}
                </MenuItem>
              ))}
              <MenuItem
                key={`${PERIOD_TYPE_CUSTOM_RANG}`}
                value={`${PERIOD_TYPE_CUSTOM_RANG}`}
                onClick={handleOpenCustomDateDialog}
              >
                {t('enum.chart_templates.period.PERIOD_TYPE_CUSTOM_RANG')}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {!isDownMd && (
          <Grid item md="auto" lg="auto" sx={{ order: { md: 4, lg: 2 }, display: 'flex', alignItems: 'center' }}>
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <div>
                <Tooltip
                  arrow
                  PopperProps={{
                    disablePortal: true
                  }}
                  onClose={handleTooltipClose}
                  open={openTooltip}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  title={
                    <Box>
                      {MACHINE_STATUS_SIMPLE_MAP.map(status => (
                        <Box
                          key={status.value}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1
                          }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              backgroundColor: status.color,
                              marginRight: 1
                            }}
                          />
                          <Typography variant="body2">{t(`enum.machine_status.${status.value}`)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  }
                >
                  <Button
                    sx={{
                      textTransform: 'none'
                    }}
                    startIcon={<InfoOutlinedIcon />}
                    onClick={handleTooltipOpen}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#000000'
                      }}
                    >
                      {t('chart_template_details.legend')}
                    </Typography>
                  </Button>
                </Tooltip>
              </div>
            </ClickAwayListener>
          </Grid>
        )}
        <Grid item xs={12} sm={12} md lg="auto" sx={{ order: { xs: 4, sm: 4, md: 5, lg: 3 }, display: 'flex' }}>
          <FormControlLabel
            data-testid="machine-status-change-checkbox-button"
            control={<Checkbox checked={checkedShowMachineStatus} onChange={handleCheckboxChange} />}
            label={t('chart_template_details.show_machine_status')}
          />
        </Grid>
        <Grid
          item
          xs="auto"
          sm="auto"
          md={0.5}
          lg="auto"
          sx={{
            order: { xs: 2, sm: 2, md: 2, lg: 4 },
            display: 'flex',
            marginTop: { xs: 2, sm: 2, md: 0, lg: 0 },
            '& button span': { marginRight: '0' }
          }}
        >
          <Button
            data-testid="refresh-info-template-button"
            startIcon={<RefreshOutlinedIcon />}
            onClick={handleRefreshInfo}
            disabled={loading}
          />
        </Grid>
        <Grid
          item
          xs
          sm={4}
          md={2}
          lg="auto"
          sx={{
            order: { xs: 3, sm: 3, md: 3, lg: 5 },
            display: 'flex',
            justifyContent: { sm: 'flex-start', md: 'flex-end' },
            marginTop: { xs: 2, sm: 2, md: 0, lg: 0 }
          }}
        >
          <Select
            id="auto-refresh-picker"
            variant="outlined"
            value={autoRefresh}
            onChange={event => setAutoRefresh(event.target.value)}
            IconComponent={ArrowDropDownIcon}
            data-testid="auto-refresh-info-picker"
            disabled={loading || interval !== PERIOD_TYPE_LAST_8_HOURS}
            sx={{
              minWidth: '50px',
              border: 'none',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '& .MuiSelect-select': {
                color: 'primary.main',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease'
              },
              '&:hover .MuiSelect-select': {
                backgroundColor: componentTheme =>
                  loading || interval !== PERIOD_TYPE_LAST_8_HOURS
                    ? 'transparent'
                    : alpha(componentTheme.palette.primary.main, 0.04)
              },
              '& .MuiSelect-icon': {
                color: componentTheme =>
                  loading || interval !== PERIOD_TYPE_LAST_8_HOURS
                    ? componentTheme.palette.action.disabled
                    : componentTheme.palette.primary.main
              }
            }}
          >
            {autoRefreshPeriods.map(item => (
              <MenuItem key={item.key} value={item.key}>
                {t(`chart_template_details.refresh.${item.key}`)}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Stack spacing={4}>
        {isDownSm ? (
          <Box sx={{ pt: 4 }}>
            <MobileSensorPortrait />
          </Box>
        ) : (
          <div style={{ position: 'relative' }} id="graph-template">
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1000
                }}
              >
                <CircularProgress disableShrink />
              </div>
            )}
            <ReactEcharts
              ref={chartRef}
              data-testid="chart-template-canvas"
              option={options}
              opts={{ renderer: 'canvas' }}
              notMerge
              lazyUpdate
              style={{
                minHeight: `${chartPixelHeightContainer}px`,
                position: 'relative',
                overflow: 'hidden'
              }}
            />
          </div>
        )}
        {!!resumedSensorMap?.size && !!sensorAverageMap?.size && (
          <ChartTemplateFooterContainer
            footerRef={footerRef}
            resumedSensorMap={resumedSensorMap}
            sensorVisibilityList={sensorVisibilityList}
            sensorAverageMap={sensorAverageMap}
            toggleLineVisibility={toggleLineVisibility}
          />
        )}
      </Stack>
      <DateTimeDialog
        open={openCustomDateDialog}
        handleClose={handleCloseCustomDateDialog}
        title={t('custom_date_dialog.title')}
        subTitle={t('custom_date_dialog.subtitle')}
        startTimeCaption={t('custom_date_dialog.start_time')}
        endTimeCaption={t('custom_date_dialog.end_time')}
        cancelCaption={t('default_actions.cancel')}
        okCaption={t('default_actions.apply')}
        setValues={handleSetValuesCustomPeriod}
        maxDays={DATA_EXPORT_MAX_PERIOD_IN_DAYS}
        language={language}
      />
    </Paper>
  );
}

ChartTemplateDetails.propTypes = {
  graphTemplate: PropTypes.shape(),
  chartRef: PropTypes.shape(),
  footerRef: PropTypes.shape(),
  interval: PropTypes.string.isRequired,
  setInterval: PropTypes.func.isRequired,
  customPeriodStart: PropTypes.string.isRequired,
  setCustomPeriodStart: PropTypes.func.isRequired,
  customPeriodEnd: PropTypes.string.isRequired,
  setCustomPeriodEnd: PropTypes.func.isRequired,
  setIsChartLoading: PropTypes.func.isRequired
};

ChartTemplateDetails.defaultProps = {
  graphTemplate: undefined,
  chartRef: null,
  footerRef: null
};
